import { UNITLESS_ROW_HEIGHT } from "@/components/nzDataGrid"
import { FONT_WEIGHT } from "@/models/typography"
import {
  black,
  gray25,
  gray50,
  gray100,
  gray200,
  gray300,
  gray400,
  gray500,
  gray600,
  gray700,
  gray800,
  green300,
  green400,
  green500,
  mustard200,
  mustard300,
  mustard500,
  primary300,
  primary400,
  primary500,
  white,
} from "@/utils/colors"

import type { Localization } from "@mui/material/locale"
import { createTheme } from "@mui/material/styles"
import type {} from "@mui/material/themeCssVarsAugmentation"
import type { DataGridProProps } from "@mui/x-data-grid-pro"
import type {} from "@mui/x-data-grid-pro/themeAugmentation"
import type {} from "@mui/x-date-pickers-pro/themeAugmentation"

import { spectrum } from "../spectrum"
import type { Spectrum } from "../styles.types"

declare module "@mui/material" {
  interface Color {
    "25": string
  }
}

declare module "@mui/material/Card" {
  interface CardPropsVariantOverrides {
    contained: true
  }
}
declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    contained: true
  }
}
// Update the Button's color prop options
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    neutral: true
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    neutral: true
  }
}

declare module "@mui/material/CircularProgress" {
  interface CircularProgressPropsColorOverrides {
    neutral: true
  }
}

declare module "@mui/material/Switch" {
  interface SwitchPropsColorOverrides {
    neutral: true
  }
}

// Update the IconButton's color prop options
declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    neutral: true
  }
}

declare module "@mui/material/ToggleButtonGroup" {
  interface ToggleButtonGroupPropsColorOverrides {
    neutral: true
  }
}

// Update the Icon's color prop options
declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    neutral: true
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    neutral: Palette["primary"]
    spectrum: Spectrum
  }

  interface PaletteOptions {
    neutral?: PaletteOptions["primary"]
    spectrum?: Partial<Spectrum>
  }

  // allow configuration using `createTheme`
  interface TypographyVariants {
    dataLabel?: React.CSSProperties
    hero?: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    dataLabel?: React.CSSProperties
    hero?: React.CSSProperties
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    dataLabel: true
    hero: true
  }
}

type OptionalOwnerState =
  | (DataGridProProps & Record<string, unknown>)
  | undefined

const baseTheme = createTheme()
const { palette } = baseTheme

export const createRetrospecTheme = (...locales: Localization[]) => {
  return createTheme(
    {
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1350,
          xl: 1536,
        },
      },
      palette: {
        primary: {
          light: gray100.hex(),
          main: gray400.hex(),
          dark: gray500.hex(),
        },
        secondary: {
          main: gray100.hex(),
        },
        neutral: palette.augmentColor({
          color: {
            light: gray300.hex(),
            main: gray500.hex(),
            dark: gray700.hex(),
          },
        }),
        success: {
          light: green300.hex(),
          main: green400.hex(),
          dark: green500.hex(),
        },
        warning: {
          light: mustard200.hex(),
          main: mustard300.hex(),
          dark: mustard500.hex(),
        },
        info: {
          light: primary300.hex(),
          main: primary400.hex(),
          dark: primary500.hex(),
        },
        common: {
          black: black.hex(),
          white: white.hex(),
        },
        grey: {
          "25": gray25.hex(),
          "50": gray50.hex(),
          "100": gray100.hex(),
          "200": gray200.hex(),
          "300": gray300.hex(),
          "400": gray400.hex(),
          "500": gray500.hex(),
          "600": gray600.hex(),
          "700": gray700.hex(),
          "800": gray800.hex(),
          "900": gray800.darken(0.1).hex(),
        },
        text: { primary: gray800.hex() },
        spectrum,
      },
      components: {
        MuiAlert: {
          styleOverrides: {
            action: ({ theme }) => ({
              paddingTop: theme.spacing(0.25),
            }),
          },
        },
        MuiAlertTitle: {
          styleOverrides: {
            root: {
              fontWeight: FONT_WEIGHT.bold,
            },
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            root: {
              "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input":
                {
                  paddingTop: "2px",
                  paddingBottom: "2px",
                },
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: gray25.hex(),
              height: "100vh",
            },
          },
        },
        MuiCircularProgress: {
          defaultProps: {
            color: "neutral",
          },
        },
        MuiButton: {
          styleOverrides: {
            startIcon: ({ theme }) => ({
              "&.MuiButton-sizeSmall": {
                marginLeft: theme.spacing(-0.5),
              },
              "&.MuiButton-sizeMedium": {
                marginLeft: theme.spacing(-1),
              },
              "&.MuiButton-sizeLarge": {
                marginLeft: theme.spacing(-1.5),
              },
            }),
            endIcon: ({ theme }) => ({
              "&.MuiButton-sizeSmall": {
                marginRight: theme.spacing(-0.5),
              },
              "&.MuiButton-sizeMedium": {
                marginRight: theme.spacing(-1),
              },
              "&.MuiButton-sizeLarge": {
                marginRight: theme.spacing(-1.5),
              },
            }),
            root: {
              boxShadow: "none",
              fontWeight: FONT_WEIGHT.bold,
              textTransform: "unset",
              "&:hover": {
                boxShadow: "none",
              },
            },
            sizeSmall: ({ theme }) => ({
              fontSize: theme.typography.pxToRem(14),
              lineHeight: theme.typography.pxToRem(20),
              padding: `${theme.typography.pxToRem(6)} ${theme.spacing(2)}`,
              "&.MuiButton-outlined": {
                padding: `calc(${theme.typography.pxToRem(6)} - 1px) ${theme.spacing(2)}`,
              },
            }),
            sizeMedium: ({ theme }) => ({
              fontSize: theme.typography.pxToRem(16),
              lineHeight: theme.typography.pxToRem(24),
              padding: theme.spacing(1, 2.5),
              "&.MuiButton-outlined": {
                padding: `calc(${theme.spacing(1)} - 1px) ${theme.spacing(2.5)}`,
              },
            }),
            sizeLarge: ({ theme }) => ({
              lineHeight: theme.typography.pxToRem(24),
              padding: theme.spacing(1.5, 3),
              "&.MuiButton-outlined": {
                padding: `calc(${theme.spacing(1.5)} - 1px) ${theme.spacing(3)}`,
              },
            }),
          },
        },
        MuiIconButton: {
          styleOverrides: {
            sizeSmall: {
              height: "32px",
              minHeight: "32px", // Ensure button doesn't collapse when in a smaller container
              minWidth: "32px",
              width: "32px",
            },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: () => ({
              textTransform: "unset",
            }),
            sizeSmall: ({ theme }) => ({
              padding: `calc(${theme.spacing(0.75)} - 1px) ${theme.spacing(1.5)}`,
              fontSize: theme.typography.pxToRem(14),
              lineHeight: theme.spacing(2.5),
            }),
            sizeMedium: ({ theme }) => ({
              padding: `calc(${theme.spacing(1)} - 1px) ${theme.spacing(1.5)}`,
              fontSize: theme.typography.pxToRem(16),
              lineHeight: theme.spacing(3),
            }),
          },
        },
        MuiStepConnector: {
          styleOverrides: {
            line: ({ theme }) => ({
              borderColor: theme.palette.grey[100],
            }),
          },
        },
        MuiStepContent: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderColor: theme.palette.grey[100],
            }),
          },
        },
        MuiStepLabel: {
          styleOverrides: {
            iconContainer: ({ theme }) => ({
              fontWeight: theme.typography.fontWeightBold,
            }),
            label: ({ theme }) => ({
              fontWeight: theme.typography.fontWeightBold,
              "&.Mui-active": {
                fontWeight: theme.typography.fontWeightBold,
              },
              "&.MuiStepLabel-alternativeLabel": {
                marginTop: theme.spacing(1),
              },
            }),
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: ({ theme }) => ({
              "&.Mui-focused": {
                color: theme.palette.text.secondary,
              },
              "&.Mui-error": {
                color: theme.palette.text.secondary,
              },
            }),
            outlined: ({ theme }) => ({
              transform: "none",
              position: "relative",
              marginBottom: theme.typography.pxToRem(4),
              pointerEvents: "auto",
            }),
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: ({ theme }) => ({
              "&.Mui-focused": {
                color: theme.palette.text.secondary,
              },
              "&.Mui-error": {
                color: theme.palette.text.secondary,
              },
            }),
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            contained: ({ theme }) => ({
              lineHeight: 1.2,
              marginLeft: theme.spacing(0.5),
              marginTop: theme.spacing(0.5),
            }),
          },
        },
        MuiSvgIcon: {
          styleOverrides: {
            fontSizeLarge: {
              fontSize: "24px",
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              color: theme.palette.grey[800],
            }),
          },
        },
        MuiListItemIcon: {
          styleOverrides: {
            root: {
              color: "inherit",
            },
          },
        },
        MuiListItemText: {
          styleOverrides: {
            root: {
              marginBottom: 0,
              marginTop: 0,
              minWidth: "auto",
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: ({ theme }) => ({
              background: theme.palette.grey[25],
              boxShadow: "none",
            }),
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            positionEnd: {
              "& .MuiIconButton-sizeSmall": {
                height: "24px",
                marginRight: "-2px",
                minHeight: "24px",
                minWidth: "24px",
                width: "24px",
              },
            },
            positionStart: {
              "& .MuiIconButton-sizeSmall": {
                height: "24px",
                minHeight: "24px",
                minWidth: "24px",
                width: "24px",
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: ({ theme }) => ({
              backgroundColor: theme.palette.common.white,
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${theme.palette.primary.main} !important`, // this is a hack to override the specificity of the Mui-focused class
              },
              "&.MuiInputBase-sizeSmall.MuiInputBase-adornedEnd": {
                paddingRight: "4.5px",
              },
              "&.MuiInputBase-sizeSmall.MuiInputBase-adornedStart": {
                paddingLeft: "8.5px",
              },
              "&.MuiInputBase-sizeSmall .MuiOutlinedInput-input": {
                paddingBottom: "4.5px",
                paddingLeft: "8.5px",
                paddingRight: "4.5px",
                paddingTop: "4.5px",
                "&.MuiInputBase-inputAdornedStart": {
                  paddingLeft: 0,
                },
                "&.MuiInputBase-inputAdornedEnd": {
                  paddingRight: 0,
                },
              },
            }),
            notchedOutline: {
              top: "0",
              borderWidth: "1px !important",
              "& legend": {
                display: "none",
              },
            },
            input: {
              height: "24px",
              paddingBottom: "8px",
              paddingLeft: "8px",
              paddingRight: "8px",
              paddingTop: "8px",
              "&.MuiInputBase-inputAdornedStart": {
                paddingLeft: 0,
              },
              "&.MuiInputBase-inputAdornedEnd": {
                paddingRight: 0,
              },
            },
          },
        },
        MuiPaper: {
          defaultProps: {
            elevation: 2,
          },
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: theme.typography.pxToRem(8),
            }),
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: ({ theme }) => ({
              fontSize: "inherit",
              letterSpacing: "inherit",
              lineHeight: "inherit",
              color: "inherit",
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: theme.palette.primary.main,
                },
            }),
          },
        },
        MuiCard: {
          variants: [
            {
              props: { variant: "contained" },
              style: {
                backgroundColor: gray25.hex(),
                borderRadius: "0.5rem",
              },
            },
          ],
        },
        MuiCardContent: {
          styleOverrides: {
            root: ({ theme }) => ({
              "&:last-child": {
                padding: theme.spacing(2),
              },
            }),
          },
        },
        MuiChip: {
          styleOverrides: {
            root: ({ theme }) => ({
              color: theme.palette.text.primary,
              "&.MuiChip-colorPrimary.MuiChip-filledPrimary": {
                backgroundColor: theme.palette.spectrum.blue[100],
              },
              "&.MuiChip-colorPrimary.MuiChip-outlinedPrimary": {
                backgroundColor: theme.palette.spectrum.blue[100],
                borderColor: theme.palette.spectrum.blue[400],
              },
              "&.MuiChip-colorSecondary.MuiChip-filledSecondary": {
                backgroundColor: theme.palette.spectrum.blue[50],
              },
              "&.MuiChip-colorSecondary.MuiChip-outlinedSecondary": {
                backgroundColor: theme.palette.spectrum.blue[50],
                borderColor: theme.palette.spectrum.blue[100],
              },
              "&.MuiChip-colorError.MuiChip-filledError": {
                backgroundColor: theme.palette.spectrum.red[100],
              },
              "&.MuiChip-colorError.MuiChip-outlinedError": {
                backgroundColor: theme.palette.spectrum.red[100],
                borderColor: theme.palette.spectrum.red[400],
              },
              "&.MuiChip-colorSuccess.MuiChip-filledSuccess": {
                backgroundColor: theme.palette.spectrum.green[100],
              },
              "&.MuiChip-colorSuccess.MuiChip-outlinedSuccess": {
                backgroundColor: theme.palette.spectrum.green[100],
                borderColor: theme.palette.spectrum.green[400],
              },
              "&.MuiChip-colorWarning.MuiChip-filledWarning": {
                backgroundColor: theme.palette.spectrum.mustard[100],
              },
              "&.MuiChip-colorWarning.MuiChip-outlinedWarning": {
                backgroundColor: theme.palette.spectrum.mustard[100],
                borderColor: theme.palette.spectrum.mustard[400],
              },
              "&.MuiChip-colorNeutral.MuiChip-filledNeutral": {
                backgroundColor: theme.palette.grey[100],
              },
              "&.MuiChip-colorNeutral.MuiChip-outlinedNeutral": {
                backgroundColor: theme.palette.grey[100],
                borderColor: theme.palette.grey[400],
              },
              "&.MuiChip-colorInfo.MuiChip-filledInfo": {
                backgroundColor: theme.palette.spectrum.blue[100],
              },
              "&.MuiChip-colorInfo.MuiChip-outlinedInfo": {
                backgroundColor: theme.palette.spectrum.blue[100],
                borderColor: theme.palette.spectrum.blue[400],
              },
            }),
            label: ({ ownerState, theme }) => ({
              fontSize: theme.typography.pxToRem(
                ownerState.size === "small" ? 14 : 16
              ),
            }),
          },
        },
        MuiTableCell: {
          styleOverrides: {
            head: {
              fontWeight: FONT_WEIGHT.bold,
            },
          },
        },
        MuiTableContainer: {
          styleOverrides: {
            root: ({ theme }) => ({
              // Paper's own radius is 8px. Data grids use theme.shape.borderRadius.
              "&.MuiPaper-root": {
                borderRadius: theme.shape.borderRadius,
              },
            }),
          },
        },
        MuiTabs: {
          styleOverrides: {
            root: ({ theme }) => ({
              minHeight: theme.typography.pxToRem(40),
            }),
          },
        },
        MuiTab: {
          styleOverrides: {
            root: ({ theme }) => ({
              height: theme.typography.pxToRem(40),
              minHeight: theme.typography.pxToRem(40),
              textTransform: "none",
              fontSize: theme.typography.pxToRem(16),
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.neutral.dark,
              "&:hover": {
                backgroundColor: theme.palette.grey[50],
              },
              "&.Mui-selected": {
                color: theme.palette.primary.main,
              },
              "&:hover.Mui-selected": {
                backgroundColor: theme.palette.spectrum.blue[50],
              },
            }),
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: ({ theme }) => ({
              gap: theme.spacing(2),
              paddingTop: theme.spacing(1),
              paddingRight: theme.spacing(2),
              paddingLeft: theme.spacing(2),
              paddingBottom: theme.spacing(2),
            }),
          },
        },
        MuiDialogContentText: {
          styleOverrides: {
            root: {
              color: "inherit",
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: ({ theme }) => ({
              ...theme.typography.h2,
              color: gray800.toString(),
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }),
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: ({ theme }) => ({
              padding: theme.spacing(0.5),
            }),
          },
        },
        MuiLink: {
          defaultProps: {
            underline: "hover",
          },
          styleOverrides: {
            root: ({ theme }) => ({
              color: theme.palette.primary.main,
            }),
          },
        },
        MuiSkeleton: {
          styleOverrides: {
            rounded: ({ theme }) => ({
              borderRadius: theme.spacing(1),
            }),
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: ({ theme }) => ({
              backgroundColor: theme.palette.common.white,
              color: theme.palette.text.primary,
              boxShadow: theme.shadows[4],
              paddingTop: theme.spacing(0.5),
              paddingBottom: theme.spacing(0.5),
              paddingLeft: theme.spacing(1.5),
              paddingRight: theme.spacing(1.5),
              fontSize: theme.typography.pxToRem(14),
            }),
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: ({ theme }) => ({
              backgroundColor: theme.palette.common.white,
              borderColor: theme.palette.grey[100],
            }),
            row: ({ theme }) => ({
              backgroundColor: theme.palette.common.white,
              "&:hover": {
                backgroundColor: theme.palette.spectrum.blue[50],
              },
              "&.Mui-hovered": {
                backgroundColor: theme.palette.spectrum.blue[50],
              },
              borderColor: theme.palette.grey[50],
            }),
            columnHeaders: ({ theme }) => ({
              backgroundColor: theme.palette.common.white,
            }),
            columnHeaderTitle: ({ theme }) => ({
              ...theme.typography.body1,
              fontWeight: theme.typography.fontWeightBold,
            }),
            columnHeaderTitleContainer: ({ theme, ownerState }) => {
              const optionalOwnerState = ownerState as OptionalOwnerState

              return {
                alignItems:
                  (optionalOwnerState?.columnHeaderHeight ?? 0) <=
                  UNITLESS_ROW_HEIGHT
                    ? "center"
                    : "flex-start",
                "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                  paddingTop:
                    (optionalOwnerState?.columnHeaderHeight ?? 0) <=
                    UNITLESS_ROW_HEIGHT
                      ? 0
                      : theme.spacing(1),
                  paddingBottom:
                    (optionalOwnerState?.columnHeaderHeight ?? 0) <=
                    UNITLESS_ROW_HEIGHT
                      ? 0
                      : theme.spacing(1),
                },
              }
            },
            columnSeparator: ({ theme, ownerState }) => {
              const optionalOwnerState = ownerState as OptionalOwnerState

              return {
                justifyContent:
                  (optionalOwnerState?.columnHeaderHeight ?? 0) <=
                  UNITLESS_ROW_HEIGHT
                    ? "center"
                    : "flex-start",
                paddingTop:
                  (optionalOwnerState?.columnHeaderHeight ?? 0) <=
                  UNITLESS_ROW_HEIGHT
                    ? 0
                    : theme.spacing(1),
              }
            },
            menuIcon: ({ theme, ownerState }) => {
              const optionalOwnerState = ownerState as OptionalOwnerState

              return {
                alignItems:
                  (optionalOwnerState?.columnHeaderHeight ?? 0) <=
                  UNITLESS_ROW_HEIGHT
                    ? "center"
                    : "flex-start",
                paddingTop:
                  (optionalOwnerState?.columnHeaderHeight ?? 0) <=
                  UNITLESS_ROW_HEIGHT
                    ? 0
                    : theme.spacing(0.5),
              }
            },
            iconButtonContainer: ({ theme, ownerState }) => {
              const optionalOwnerState = ownerState as
                | (DataGridProProps & Record<string, unknown>)
                | undefined

              return {
                paddingTop:
                  (optionalOwnerState?.columnHeaderHeight ?? 0) <=
                  UNITLESS_ROW_HEIGHT
                    ? 0
                    : theme.spacing(0.5),
              }
            },
            filterForm: ({ theme }) => ({
              columnGap: theme.spacing(1),
            }),
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: () => ({
              borderRight: "none",
            }),
          },
        },
      },
      typography: {
        fontFamily: "'Lato', Arial",
        hero: {
          lineHeight: 1.6,
          fontSize: baseTheme.typography.pxToRem(32),
          fontWeight: FONT_WEIGHT.normal,
        },
        h1: {
          lineHeight: 1.6,
          fontSize: baseTheme.typography.pxToRem(26),
          fontWeight: FONT_WEIGHT.bold,
        },
        h2: {
          lineHeight: 1.4,
          fontSize: baseTheme.typography.pxToRem(20),
          fontWeight: FONT_WEIGHT.normal,
        },
        h3: {
          lineHeight: 1.5,
          fontSize: baseTheme.typography.pxToRem(18),
          fontWeight: FONT_WEIGHT.normal,
        },
        h4: {
          lineHeight: 1.5,
          fontSize: baseTheme.typography.pxToRem(16),
          fontWeight: FONT_WEIGHT.normal,
        },
        h5: {
          lineHeight: 1.4,
          fontSize: baseTheme.typography.pxToRem(14),
          fontWeight: FONT_WEIGHT.bold,
        },
        h6: {
          lineHeight: 1.4,
          fontSize: baseTheme.typography.pxToRem(14),
          fontWeight: FONT_WEIGHT.normal,
        },
        body1: {
          fontSize: baseTheme.typography.pxToRem(16),
          lineHeight: 1.5,
          fontWeight: FONT_WEIGHT.normal,
        },
        body2: {
          fontSize: baseTheme.typography.pxToRem(14),
          lineHeight: 1.5,
          fontWeight: FONT_WEIGHT.normal,
        },
        caption: {
          fontSize: baseTheme.typography.pxToRem(12),
          lineHeight: 1.2,
          fontWeight: FONT_WEIGHT.normal,
        },
        dataLabel: {
          fontSize: baseTheme.typography.pxToRem(10),
          lineHeight: 1.2,
          fontWeight: FONT_WEIGHT.normal,
        },
      },
    },
    ...locales
  )
}
